import classNames from "classnames";
import React from "react";
import { Menu } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import { subpageContext } from "components/layout/ApplicationLayout";
import layoutStyle from "components/layout/layout.scss";
import ManageLicenseKeyView from "components/licenses/assign-bms-keys/ManageLicenseKeyView";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import { Result } from "components/reports/ManageEditReportView";
import DeleteItemStatusDialog from "components/status-dialog/DeleteItemStatusDialog";
import style from "components/tenants/tenants.scss";
import { AUTH_LICENSE_ASSIGN, AUTH_LICENSE_KEY_MANAGEMENT } from "domain/authority";
import { Key, LicenseData } from "domain/licenses";
import { licenseService } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface LicenseKeyData {
    licenseKey: Key;
    availableLicenses: LicenseData[];
    fromBms: boolean;
    setRequestFailureMessage?: (error: string) => void;
    setReloadLicenseKeys?: (toReload: boolean) => void;
}

export const LicenseKeyMenuItems = (props: LicenseKeyData): JSX.Element => {
    const context = React.useContext(subpageContext);
    const { t } = useTranslation();
    const [editLicenseKeyVisible, setEditLicenseKeyVisible] = React.useState(false);
    const [deleteLicenseKeyModalVisible, setDeleteLicenseKeyModalVisible] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [licenseKeyNotDeleted, setLicenseKeyNotDeleted] = React.useState<boolean>(false);
    const [okClicked, setOkClicked] = React.useState<boolean>(false);
    const theme = useTheme();

    function onSuccess() {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE,
            action: Action.UPDATE_LICENSE_KEY,
        });
        context?.setSubpage({
            content: undefined,
        });
        setEditLicenseKeyVisible(true);
    }

    function onFetchLicensesFailure() {
        setEditLicenseKeyVisible(false);
        context?.setSubpage({
            content: undefined,
        });
    }

    const deleteLicenseKey = () => {
        const abortController = new AbortController();
        licenseService
            .deleteLicenseKeys([props.licenseKey.licenseKey], abortController)
            .then(() => {
                setDeleteLicenseKeyModalVisible(false);
                setResult({
                    title: t("DeleteLicenseKey.title"),
                    message: t("DeleteLicenseKey.deletedMessage", { licenseKey: props.licenseKey.licenseKey }),
                });
                setOkClicked(false);
                props.setReloadLicenseKeys?.(true);
                toast(
                    <SuccessIcon
                        successClass={layoutStyle.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={t("DeleteLicenseKey.deletedMessage", { licenseKey: props.licenseKey.licenseKey })}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layoutStyle.customToastSuccessMessage,
                    }
                );
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setOkClicked(false);
                    setDeleteLicenseKeyModalVisible(false);
                    setResult({
                        title: t("DeleteLicenseKey.licenseKeyNotDeletedTitle"),
                        message: t("DeleteUser.userDeleted.failureMessage"),
                    });
                }
            });
    };

    function updateLicenseKeysStatus(enable: boolean) {
        setLoading(true);
        const abortController = new AbortController();
        const message = enable
            ? t("LicenseKeys.singleKeyEnabledSuccessMessage")
            : t("LicenseKeys.singleKeyDisabledSuccessMessage");
        licenseService
            .updateLicenseKeysStatus([props.licenseKey.licenseKey], enable, abortController)
            .then(() => {
                setLoading(false);
                props.setReloadLicenseKeys?.(true);
                toast(
                    <SuccessIcon
                        successClass={layoutStyle.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={message}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layoutStyle.customToastSuccessMessage,
                    }
                );
            })
            .catch(() => {
                setLoading(false);
                props.setRequestFailureMessage?.(t("LicenseKeys.requestFailed"));
            });
    }

    React.useEffect(() => {
        if (editLicenseKeyVisible && context?.setSubpage) {
            context.setSubpage({
                content: (
                    <ManageLicenseKeyView
                        isEdit={true}
                        licenseKeyData={props.licenseKey}
                        onSuccess={onSuccess}
                        onFetchLicensesFailure={onFetchLicensesFailure}
                        fromBms={props.fromBms}
                        availableLicenses={props.availableLicenses}
                    />
                ),
                isModal: false,
            });
        }
    }, [editLicenseKeyVisible, props.availableLicenses]);

    return (
        <>
            <div className={classNames(style.tenantIcon)}>
                <Menu className={style.kebabMenu}>
                    <ul>
                        {userSessionService.userHasAllAuthorities([AUTH_LICENSE_ASSIGN]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    className={style.menuItem}
                                    onClick={() => setEditLicenseKeyVisible(true)}
                                    data-testid={
                                        props.fromBms
                                            ? testIds.workArea.license.bms.table.kebabMenu.editButton
                                            : testIds.workArea.license.serialNumber.table.kebabMenu.editButton
                                    }
                                >
                                    {t("Common.edit")}
                                </MenuItemButton>
                            </li>
                        )}
                        {userSessionService.userHasAllAuthorities([AUTH_LICENSE_ASSIGN]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    className={classNames(style.menuItem, {
                                        [style.deleteListItem]: props.licenseKey.enabled,
                                    })}
                                    onClick={() =>
                                        props.licenseKey.enabled
                                            ? updateLicenseKeysStatus(false)
                                            : updateLicenseKeysStatus(true)
                                    }
                                    data-testid={
                                        props.fromBms
                                            ? testIds.workArea.license.bms.table.kebabMenu.disableButton
                                            : testIds.workArea.license.serialNumber.table.kebabMenu.disableButton
                                    }
                                >
                                    {props.licenseKey.enabled ? t("LicenseKeys.deactivate") : t("LicenseKeys.activate")}
                                </MenuItemButton>
                            </li>
                        )}
                        {userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    tag={"button"}
                                    className={style.menuItem}
                                    onClick={(e) => {
                                        usageStatisticsService.sendEvent({
                                            category: Category.LICENSE_KEYS,
                                            action: Action.DELETE_SINGLE_LICENSE_KEY,
                                        });
                                        setDeleteLicenseKeyModalVisible(true);
                                        e.preventDefault();
                                    }}
                                    data-testid={
                                        props.fromBms
                                            ? testIds.workArea.license.bms.table.kebabMenu.deleteButton
                                            : testIds.workArea.license.serialNumber.table.kebabMenu.deleteButton
                                    }
                                >
                                    {t("Common.delete")}
                                </MenuItemButton>
                            </li>
                        )}
                    </ul>
                </Menu>
                <DeleteItemStatusDialog
                    title={result.title}
                    modalDisplayed={licenseKeyNotDeleted}
                    setModalDisplayed={() => setLicenseKeyNotDeleted(false)}
                    handleCommand={deleteLicenseKey}
                    ditchLink={true}
                />
                <DeleteConfirmationDialog
                    title={t("DeleteLicenseKey.title")}
                    introductionMessage={t("DeleteLicenseKey.introductionMessage", {
                        licenseKey: props.licenseKey.licenseKey,
                    })}
                    modalDisplayed={deleteLicenseKeyModalVisible}
                    setModalDisplayed={setDeleteLicenseKeyModalVisible}
                    handleCommand={deleteLicenseKey}
                    loading={okClicked}
                    confirmationMessage={t("DeleteLicenseKey.confirmationMessage")}
                />

                <Modal isOpen={loading} hideModal={() => setLoading(false)}>
                    <LoadingIndicator />
                </Modal>
            </div>
        </>
    );
};
